import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { formatMeterCents } from '../../utils/gamingCurrency'
import { formatSydneyDateTime, formatSydneyTime } from '../../utils/sydneyDateTime'
import { buildGamingDataUrl } from '../monitor-safe/alertContextUtils'

const PAGE_SIZE = 50

function allocNum(row, ...keys) {
  if (!row) return 0
  for (const k of keys) {
    if (row[k] != null && row[k] !== '') {
      const n = Number(row[k])
      if (Number.isFinite(n)) return n
    }
  }
  const lower = Object.create(null)
  for (const [k, v] of Object.entries(row)) {
    if (typeof k === 'string') lower[k.toLowerCase()] = v
  }
  for (const k of keys) {
    const v = lower[String(k).toLowerCase()]
    if (v != null && v !== '') {
      const n = Number(v)
      if (Number.isFinite(n)) return n
    }
  }
  return 0
}

function parseDate(val) {
  if (!val) return null
  try {
    const s = typeof val === 'string' ? (val.includes('T') ? val : val.replace(' ', 'T')) : val
    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? null : d
  } catch {
    return null
  }
}

function formatHourLabel(h) {
  const n = Number(h)
  if (!Number.isFinite(n)) return String(h)
  return `${String(n).padStart(2, '0')}:00`
}

function focusSummary({ selectedEgmpks, selectedHours }) {
  const parts = []
  if (selectedEgmpks?.length) {
    parts.push(`${selectedEgmpks.length} EGM${selectedEgmpks.length === 1 ? '' : 's'}`)
  }
  if (selectedHours?.length) {
    const hrs = [...selectedHours].sort((a, b) => a - b).map(formatHourLabel)
    parts.push(`hour${selectedHours.length === 1 ? '' : 's'} ${hrs.join(', ')}`)
  }
  return parts.join(' · ')
}

function cashTotal(row) {
  return (
    allocNum(row, 'CashIn_alloc', 'cashin_alloc') + allocNum(row, 'CashDrop_alloc', 'cashdrop_alloc')
  )
}

async function fetchFocusSessions({ tradingDay, machineClass, selectedEgmpks, selectedHours, page = 1 }) {
  const params = new URLSearchParams({
    trading_day: tradingDay,
    page: String(page),
    limit: String(PAGE_SIZE),
    include_intervals: 'true',
  })
  if (machineClass && machineClass !== 'all') params.set('machine_class', machineClass)
  if (selectedEgmpks?.length) params.set('egmpks', selectedEgmpks.join(','))
  if (selectedHours?.length) params.set('hours', selectedHours.map(String).join(','))
  const res = await authFetch(getLocalApiUrl(`/api/gaming/combined/by-session?${params}`))
  const json = await res.json()
  if (!json.success) throw new Error(json.error || 'Failed to load sessions')
  return {
    rows: Array.isArray(json.data) ? json.data : [],
    total: Number(json.total) || 0,
    hint: json.hint || null,
  }
}

function SessionIntervalTimeline({ spk, ivs }) {
  if (!ivs.length) {
    return <p className="text-sm text-gray-500">No interval detail returned for this session.</p>
  }

  return (
    <div className="overflow-x-auto rounded border border-slate-200 bg-white">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-2 py-1.5 text-left">Interval</th>
            <th className="px-2 py-1.5 text-right">Turnover</th>
            <th className="px-2 py-1.5 text-right">CreditWin</th>
            <th className="px-2 py-1.5 text-right" title="Meter stroke delta (games/handle count)">
              Stroke
            </th>
            <th className="px-2 py-1.5 text-right">CashIn</th>
            <th className="px-2 py-1.5 text-right">Cashless</th>
            <th className="px-2 py-1.5 text-right">Tickets</th>
            <th className="px-2 py-1.5 text-right">Cum TO</th>
            <th className="px-2 py-1.5 text-right">Cum Win</th>
            <th className="px-2 py-1.5 text-right">Cum Stroke</th>
            <th className="px-2 py-1.5 text-right">Cum Net</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {ivs.map((iv, i) => {
            const d = parseDate(iv.IntervalStart ?? iv.intervalstart)
            const net = Number(iv.Net ?? NaN)
            const netVal = Number.isFinite(net) ? net : allocNum(iv, 'Turnover_alloc', 'turnover_alloc') - allocNum(iv, 'CreditWin_alloc', 'creditwin_alloc')
            const ivStroke = allocNum(iv, 'Stroke_alloc', 'stroke_alloc')
            const cumStroke = Number(iv.CumStroke ?? iv.cumstroke ?? 0) || 0
            const ivCash = allocNum(iv, 'CashIn_alloc', 'cashin_alloc') + allocNum(iv, 'CashDrop_alloc', 'cashdrop_alloc')
            return (
              <tr key={`${spk}-${i}`}>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  {d ? formatSydneyTime(d, { second: '2-digit' }) : '—'}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {formatMeterCents(allocNum(iv, 'Turnover_alloc', 'turnover_alloc'))}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {formatMeterCents(allocNum(iv, 'CreditWin_alloc', 'creditwin_alloc'))}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {ivStroke > 0 ? Math.round(ivStroke).toLocaleString() : '—'}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {ivCash > 0 ? formatMeterCents(ivCash) : '—'}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {formatMeterCents(allocNum(iv, 'CashlessIn_alloc', 'cashlessin_alloc'))}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {formatMeterCents(allocNum(iv, 'TicketsIn_alloc', 'ticketsin_alloc'))}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {formatMeterCents(Number(iv.CumTurnover ?? iv.cumturnover ?? 0))}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {formatMeterCents(Number(iv.CumCreditWin ?? iv.cumcreditwin ?? 0))}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {cumStroke > 0 ? Math.round(cumStroke).toLocaleString() : '—'}
                </td>
                <td
                  className={`px-2 py-1.5 text-right tabular-nums font-medium ${
                    netVal >= 0 ? 'text-red-700' : 'text-emerald-700'
                  }`}
                >
                  {formatMeterCents(netVal)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default function TurnoverFocusSessions({
  tradingDay,
  machineClass = 'all',
  selectedEgmpks = [],
  selectedHours = [],
  allocateLabel,
}) {
  const hasFocus = selectedEgmpks.length > 0 || selectedHours.length > 0
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hint, setHint] = useState(null)
  const [expandedSessions, setExpandedSessions] = useState(() => new Set())

  const meterOnly = allocateLabel === 'Meter only — allocate not run'
  const summary = useMemo(
    () => focusSummary({ selectedEgmpks, selectedHours }),
    [selectedEgmpks, selectedHours]
  )

  const gamingDataUrl = useMemo(() => {
    if (!tradingDay) return '/monitor-safe/gaming-data?tab=combined'
    return buildGamingDataUrl({
      tab: 'combined',
      trading_day: tradingDay,
      view: 'sessions',
      egmpks: selectedEgmpks.length ? selectedEgmpks.join(',') : undefined,
      hours: selectedHours.length ? selectedHours.map(String).join(',') : undefined,
      machine_class: machineClass !== 'all' ? machineClass : undefined,
      egm_pk: selectedEgmpks.length === 1 ? selectedEgmpks[0] : undefined,
    })
  }, [tradingDay, selectedEgmpks, selectedHours, machineClass])

  const toggleSessionExpand = useCallback((sessionPk) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev)
      if (next.has(sessionPk)) next.delete(sessionPk)
      else next.add(sessionPk)
      return next
    })
  }, [])

  const load = useCallback(async () => {
    if (!tradingDay || !hasFocus || meterOnly) {
      setRows([])
      setTotal(0)
      setHint(null)
      setError(null)
      setExpandedSessions(new Set())
      return
    }
    setLoading(true)
    setError(null)
    try {
      const result = await fetchFocusSessions({
        tradingDay,
        machineClass,
        selectedEgmpks,
        selectedHours,
        page,
      })
      setRows(result.rows)
      setTotal(result.total)
      setHint(result.hint)
      setExpandedSessions(new Set())
    } catch (e) {
      setError(e.message)
      setRows([])
      setTotal(0)
      setExpandedSessions(new Set())
    } finally {
      setLoading(false)
    }
  }, [tradingDay, machineClass, selectedEgmpks, selectedHours, page, hasFocus, meterOnly])

  useEffect(() => {
    setPage(1)
  }, [tradingDay, machineClass, selectedEgmpks, selectedHours])

  useEffect(() => {
    load()
  }, [load])

  if (!hasFocus) return null

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const sessionTotals = rows.reduce(
    (acc, r) => {
      acc.turnover += allocNum(r, 'Turnover_alloc', 'turnover_alloc')
      acc.win += allocNum(r, 'CreditWin_alloc', 'creditwin_alloc')
      acc.cash += cashTotal(r)
      return acc
    },
    { turnover: 0, win: 0, cash: 0 }
  )
  const colCount = 11

  return (
    <div className="bg-white shadow rounded-lg border border-indigo-100 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">Combined sessions (focus)</h2>
          <p className="text-[11px] text-indigo-700 mt-0.5">{summary}</p>
          {!meterOnly && total > 0 && (
            <p className="text-[11px] text-gray-500 mt-0.5">
              {total} session{total === 1 ? '' : 's'}
              {rows.length > 0 && total <= PAGE_SIZE ? (
                <>
                  {' · '}
                  TO {formatMeterCents(sessionTotals.turnover)}
                  {' · '}
                  Win {formatMeterCents(sessionTotals.win)}
                </>
              ) : null}
              {' · '}
              Click a row with 2+ intervals to expand
            </p>
          )}
        </div>
        <Link
          to={gamingDataUrl}
          className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 whitespace-nowrap"
        >
          Open in Gaming Data
          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
        </Link>
      </div>

      {meterOnly ? (
        <div className="px-4 py-4 text-sm text-gray-600">
          Meter data is loaded but <strong>Process &amp; Combine</strong> has not been run for this day.
          Run it on the{' '}
          <Link to={gamingDataUrl} className="text-primary-600 hover:underline">
            Combined tab
          </Link>{' '}
          to see session-level metering attribution.
        </div>
      ) : loading ? (
        <div className="px-4 py-6 text-sm text-gray-500">Loading sessions…</div>
      ) : error ? (
        <div className="px-4 py-4 text-sm text-red-700">{error}</div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-4 text-sm text-gray-500">
          {hint || 'No combined sessions match this focus. Sessions may not overlap the selected hour(s), or combine has not been run.'}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm divide-y divide-gray-100">
              <thead className="bg-gray-50 text-gray-600 text-xs">
                <tr>
                  <th className="px-2 py-2 text-left w-8" aria-label="Expand" />
                  <th className="px-3 py-2 text-left font-medium">Session start</th>
                  <th className="px-3 py-2 text-left font-medium">EgmPK</th>
                  <th className="px-3 py-2 text-left font-medium">Player</th>
                  <th className="px-3 py-2 text-right font-medium">Intervals</th>
                  <th className="px-3 py-2 text-right font-medium">Duration</th>
                  <th className="px-3 py-2 text-right font-medium">Turnover</th>
                  <th className="px-3 py-2 text-right font-medium">CreditWin</th>
                  <th className="px-3 py-2 text-right font-medium">Cash / Cashless / Tickets</th>
                  <th className="px-3 py-2 text-right font-medium">Net (TO−Win)</th>
                  <th className="px-3 py-2 text-left font-medium min-w-[7rem]">Play shape</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((r) => {
                  const spk = r.SessionPK ?? r.sessionpk
                  const open = expandedSessions.has(spk)
                  const isUnc =
                    r.IsUncarded === true ||
                    r.IsUncarded === 't' ||
                    r.MemberID === '__UNCARDED__' ||
                    String(spk || '').startsWith('UNC_')
                  const turnover = allocNum(r, 'Turnover_alloc', 'turnover_alloc')
                  const win = allocNum(r, 'CreditWin_alloc', 'creditwin_alloc')
                  const cash = cashTotal(r)
                  const cashless = allocNum(r, 'CashlessIn_alloc', 'cashlessin_alloc')
                  const tickets = allocNum(r, 'TicketsIn_alloc', 'ticketsin_alloc')
                  const start = parseDate(r.SessionStart || r.FirstInterval || r.sessionstart)
                  const end = parseDate(r.SessionEnd || r.LastInterval || r.sessionend)
                  const mins =
                    start && end
                      ? Math.max(0, Math.round((end - start) / 60000))
                      : r.EnrichedSessionMinutes != null
                        ? Number(r.EnrichedSessionMinutes)
                        : null
                  const ivs = Array.isArray(r.intervals) ? r.intervals : []
                  const intervalCount = Number(r.IntervalCount ?? r.intervalcount)
                  const intervalDisplay =
                    Number.isFinite(intervalCount) && intervalCount > 0 ? intervalCount : ivs.length || intervalCount || 0
                  const canExpand = intervalDisplay >= 2
                  const maxTo = Math.max(1, ...ivs.map((iv) => allocNum(iv, 'Turnover_alloc', 'turnover_alloc')))
                  const net = turnover - win

                  return (
                    <React.Fragment key={spk}>
                      <tr
                        className={`${canExpand ? 'cursor-pointer hover:bg-slate-50' : ''} ${open ? 'bg-slate-50' : ''}`}
                        onClick={canExpand ? () => toggleSessionExpand(spk) : undefined}
                      >
                        <td className="px-2 py-2 text-gray-500">
                          {canExpand ? (open ? '▾' : '▸') : ''}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                          {start ? formatSydneyDateTime(start) : '—'}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{r.EgmPK ?? r.egmpk ?? '—'}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium ${
                              isUnc ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                            }`}
                          >
                            {isUnc ? 'Uncarded' : r.MemberID || r.memberid || 'Carded'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{intervalDisplay || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{mins != null ? `${mins}m` : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatMeterCents(turnover)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatMeterCents(win)}</td>
                        <td className="px-3 py-2 text-xs text-right whitespace-nowrap tabular-nums">
                          {formatMeterCents(cash)} / {formatMeterCents(cashless)} / {formatMeterCents(tickets)}
                        </td>
                        <td
                          className={`px-3 py-2 text-right tabular-nums font-medium ${
                            net >= 0 ? 'text-red-700' : 'text-emerald-700'
                          }`}
                        >
                          {formatMeterCents(net)}
                        </td>
                        <td className="px-3 py-2">
                          {ivs.length > 0 ? (
                            <div className="flex items-end gap-px h-8" title="Turnover per interval (oldest → newest)">
                              {ivs.map((iv, i) => {
                                const h = Math.max(
                                  2,
                                  Math.round((allocNum(iv, 'Turnover_alloc', 'turnover_alloc') / maxTo) * 28)
                                )
                                const hasCash =
                                  allocNum(iv, 'CashIn_alloc', 'cashin_alloc') +
                                    allocNum(iv, 'CashDrop_alloc', 'cashdrop_alloc') +
                                    allocNum(iv, 'CashlessIn_alloc', 'cashlessin_alloc') +
                                    allocNum(iv, 'TicketsIn_alloc', 'ticketsin_alloc') >
                                  0
                                return (
                                  <div
                                    key={i}
                                    className={`flex-1 min-w-[3px] rounded-sm ${hasCash ? 'bg-amber-500' : 'bg-slate-400'}`}
                                    style={{ height: `${h}px` }}
                                  />
                                )
                              })}
                            </div>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                      {open && canExpand && (
                        <tr className="bg-slate-50/80">
                          <td colSpan={colCount} className="px-4 py-3">
                            <div className="text-xs text-gray-600 mb-2">
                              Play timeline · cumulative turnover vs wins
                            </div>
                            <SessionIntervalTimeline spk={spk} ivs={ivs} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
          {pageCount > 1 && (
            <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between text-xs text-gray-600">
              <span>
                Page {page} of {pageCount} · {total} total
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="px-2 py-1 border rounded hover:bg-gray-50 disabled:opacity-40"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-2 py-1 border rounded hover:bg-gray-50 disabled:opacity-40"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
